import HomePageContainer from '../../containers/Home'
import NotFound from '../NotFound'
import { fetchPageEntitlements } from '../../helpers/UtilityHelper'
import Unauthorized from '../Unauthorized'
import {
  SINGLE_PLAYGROUND_VIEW_AI,
  BULK_TESTS_VIEW_AI,
  SINGLE_PLAYGROUND_VIEW_VIDEO_AI,
} from '../../constants/routes'
import { MODERATED_ASSETS_ROUTE } from '../../moderation/constants/moderationConstants'
import { lazyRoute } from './LazyRoute'

const groupReducerLoaders = {
  groups: () =>
    import(/* webpackChunkName: "route-groups" */ '../../store/groups/reducer'),
}

const projectTaskReducerLoaders = {
  task: () =>
    import(/* webpackChunkName: "route-projects" */ '../../store/task/reducer'),
}

const annotationReducerLoaders = {
  annotation: () =>
    import(
      /* webpackChunkName: "route-annotation" */ '../../store/annotation/reducer'
    ),
}

const publishReducerLoaders = {
  publish: () =>
    import(
      /* webpackChunkName: "route-publish" */ '../../store/publish/reducer'
    ),
  assetPublishUpload: () =>
    import(
      /* webpackChunkName: "route-publish" */ '../../store/publishUpload/reducer'
    ),
  channelPublish: () =>
    import(
      /* webpackChunkName: "route-publish" */ '../../store/channelUpload/reducer'
    ),
}

const assetLifecycleReducerLoaders = {
  assetLifecycle: () =>
    import(
      /* webpackChunkName: "route-asset-lifecycle" */ '../../store/assetLifecycle/reducer'
    ),
}

const tenantReducerLoaders = {
  tenant: () =>
    import(
      /* webpackChunkName: "route-help" */ '../../store/tenant/actionCreator'
    ),
}

const SearchPage = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-search", webpackPrefetch: true */ '../NewSearch'
    ),
)
const PencilPage = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-subhub-pencil" */ '../../subhubs/pencil/pages'
    ),
)
const ModeratedAssetsPage = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-moderated-assets" */ '../../moderation/pages'
    ),
)
const BrandPage = lazyRoute(
  () =>
    import(/* webpackChunkName: "route-brand" */ '../BrandLayout/BrandPage'),
)
const GroupsPage = lazyRoute(
  () => import(/* webpackChunkName: "route-groups" */ '../Groups'),
  groupReducerLoaders,
)
const GroupDashboardPage = lazyRoute(
  () =>
    import(/* webpackChunkName: "route-groups" */ '../Groups/Group/dashboard'),
  groupReducerLoaders,
)
const Boards = lazyRoute(
  () =>
    import(/* webpackChunkName: "route-boards" */ '../../containers/Boards'),
)
const BoardDashboard = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-boards" */ '../../containers/Boards/Board/BoardDashboard'
    ),
)
const NestedProjectViews = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-projects" */ '../ProjectsPage/project/NestedProjectViews'
    ),
  projectTaskReducerLoaders,
)
const ProjectsPage = lazyRoute(
  () => import(/* webpackChunkName: "route-projects" */ '../ProjectsPage'),
)
const Annotation = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-annotation" */ '../../containers/Annotation'
    ),
  annotationReducerLoaders,
)
const UsageRights = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-usage-rights" */ '../UsageRights/UsageRights'
    ),
)
const UsageRightsCreate = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-usage-rights" */ '../UsageRights/UsageRightsCreate'
    ),
)
const Publish = lazyRoute(
  () => import(/* webpackChunkName: "route-publish" */ '../Publish/index'),
  publishReducerLoaders,
)
const ChannelPublish = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-publish" */ '../Publish/ChannelUpload/ChannelPublish'
    ),
  publishReducerLoaders,
)
const PublishUpload = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-publish" */ '../Publish/Upload/PublishUpload'
    ),
  publishReducerLoaders,
)
const AssetClassification = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-classification" */ '../Classification/AssetClassification'
    ),
)
const AssetLifecycleDashboard = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-asset-lifecycle" */ '../AssetLifecycleDashboard'
    ),
  assetLifecycleReducerLoaders,
)
const SettingsLandingPage = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-settings" */ '../../settings/pages/SettingsLandingPage'
    ),
)
const PromptDetailsForm = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-settings" */ '../../settings/pages/PromptDetailsForm'
    ),
)
const HelpPage = lazyRoute(
  () => import(/* webpackChunkName: "route-help" */ '../../containers/Help'),
  tenantReducerLoaders,
)
const PromptPlayground = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-genai-playground" */ '../../genai/pages/PromptPlayground'
    ),
)
const BulkTestsListView = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-genai-bulk-tests" */ '../../genai/pages/BulkTestsListView'
    ),
)
const NewBulkTestView = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-genai-bulk-tests" */ '../../genai/pages/NewBulkTestView'
    ),
)
const BulkTestReviewPage = lazyRoute(
  () =>
    import(
      /* webpackChunkName: "route-genai-bulk-tests" */ '../../genai/pages/BulkTestReviewPage'
    ),
)

const navRoutes = [
  {
    path: '/',
    exact: true,
    routeName: 'Home',
    component: HomePageContainer,
    tooltip: 'Home',
    displayName: 'Home',
    adGroups: (env) => fetchPageEntitlements(env, 'assethubADGroups'),
  },
  {
    path: '/search',
    routeName: 'search',
    displayName: 'Search',
    component: SearchPage,
    adGroups: (env) => fetchPageEntitlements(env, 'search'),
    exact: true,
  },
  {
    path: '/search/:assetId/metadata',
    routeName: 'Search',
    component: SearchPage,
    tooltip: 'Search',
    displayName: 'Search',
    adGroups: (env = {}) => fetchPageEntitlements(env, 'search'),
  },
  {
    path: MODERATED_ASSETS_ROUTE,
    routeName: 'moderated_assets',
    displayName: 'Moderated Assets',
    component: ModeratedAssetsPage,
    adGroups: (env) => fetchPageEntitlements(env, 'moderation'),
    exact: true,
  },
  {
    path: '/subhubs/pencil',
    routeName: 'subhubs_pencil',
    displayName: 'Subhubs Pencil',
    component: PencilPage,
    adGroups: (env) => fetchPageEntitlements(env, 'search'),
    exact: true,
  },
  {
    path: '/brands/:brandSlug',
    routeName: 'Brand',
    displayName: 'Brand',
    component: BrandPage,
    adGroups: (env) => fetchPageEntitlements(env, 'search'),
    exact: true,
  },
  {
    path: '/groups',
    routeName: 'groups',
    displayName: 'Groups',
    component: GroupsPage,
    adGroups: (env) => fetchPageEntitlements(env, 'groups'),
    exact: true,
  },
  {
    path: '/groups/:groupId/dashboard',
    routeName: 'groups',
    displayName: 'Groups',
    component: GroupDashboardPage,
    adGroups: (env) => fetchPageEntitlements(env, 'groups'),
    exact: true,
  },
  {
    path: '/boards',
    routeName: 'boards',
    displayName: 'Boards',
    component: Boards,
    adGroups: (env) => fetchPageEntitlements(env, 'boards'),
    exact: true,
  },
  {
    path: '/boards/:boardId/dashboard',
    routeName: 'boards',
    displayName: 'Boards',
    component: BoardDashboard,
    adGroups: (env) => fetchPageEntitlements(env, 'boards'),
    exact: true,
  },
  {
    path: '/project/:projectId/:pageName/*',
    routeName: 'projects',
    tooltip: 'Projects',
    displayName: 'Projects',
    component: NestedProjectViews,
    adGroups: (env) => fetchPageEntitlements(env, 'projects'),
  },
  {
    path: '/projects',
    routeName: 'projects',
    tooltip: 'Projects',
    displayName: 'Projects',
    component: ProjectsPage,
    adGroups: (env) => fetchPageEntitlements(env, 'projects'),
    exact: true,
  },
  {
    path: '/annotator/:projectId/:jobId',
    routeName: 'annotation',
    tooltip: 'Annotation',
    displayName: 'Annotation',
    component: Annotation,
    adGroups: (env) => fetchPageEntitlements(env, 'projects'),
  },
  {
    path: '/annotator/:projectId/:jobId/assetUniqueId/:assetUniqueId/:revisionNumber/:pageNumber',
    routeName: 'annotation',
    tooltip: 'Annotation',
    displayName: 'Annotation',
    component: Annotation,
    adGroups: (env) => fetchPageEntitlements(env, 'projects'),
  },
  {
    path: '/usageRights',
    routeName: 'usageRights',
    component: UsageRights,
    adGroups: (env) => fetchPageEntitlements(env, 'usageRights'),
    exact: true,
  },
  {
    path: '/usageRights/create',
    routeName: 'usageRightsCreate',
    component: UsageRightsCreate,
    adGroups: (env) => fetchPageEntitlements(env, 'usageRights'),
    exact: true,
  },
  {
    path: '/usageRights/details/:drmId',
    routeName: 'usageRightsEdit',
    component: UsageRightsCreate,
    adGroups: (env) => fetchPageEntitlements(env, 'usageRights'),
    exact: true,
  },
  {
    path: '/publishing',
    routeName: 'publish',
    component: Publish,
    adGroups: (env) => fetchPageEntitlements(env, 'publish'),
    exact: true,
  },
  {
    path: '/publishing/:asset_id',
    routeName: 'publishAsset',
    component: Publish,
    adGroups: (env) => fetchPageEntitlements(env, 'publish'),
    exact: true,
  },
  {
    path: '/publishing/channelPublish',
    routeName: 'publishAsset',
    component: ChannelPublish,
    adGroups: undefined,
    exact: true,
  },
  {
    path: '/publishing/publishUpload',
    routeName: 'publishAsset',
    component: PublishUpload,
    adGroups: undefined,
    exact: true,
  },
  {
    path: '/classification',
    routeName: 'Classification',
    component: AssetClassification,
    adGroups: (env) => fetchPageEntitlements(env, 'classification'),
    exact: true,
  },
  {
    path: '/asset-lifecycle-dashboard',
    routeName: 'assetLifecycleDashboard',
    component: AssetLifecycleDashboard,
    adGroups: (env) => fetchPageEntitlements(env, 'assetLifecycleDashboard'),
    exact: true,
  },
  {
    path: '/settings',
    routeName: 'Settings',
    component: SettingsLandingPage,
    adGroups: (env) => fetchPageEntitlements(env, 'settings'),
    exact: true,
  },
  {
    path: '/settings/prompts/create',
    routeName: 'prompts_create',
    component: PromptDetailsForm,
    adGroups: (env) => fetchPageEntitlements(env, 'settings'),
    exact: true,
  },
  {
    path: '/settings/prompts/:promptId',
    routeName: 'prompts_edit',
    component: PromptDetailsForm,
    adGroups: (env) => fetchPageEntitlements(env, 'settings'),
    exact: true,
  },
  {
    path: '/help',
    routeName: 'help',
    component: HelpPage,
    adGroups: (env) => fetchPageEntitlements(env, 'help'),
    exact: true,
  },
  {
    path: SINGLE_PLAYGROUND_VIEW_AI,
    routeName: 'ai_imagetotext',
    component: PromptPlayground,
    adGroups: (env) => fetchPageEntitlements(env, 'ai_imagetotext'),
    exact: true,
  },
  {
    path: SINGLE_PLAYGROUND_VIEW_VIDEO_AI,
    routeName: 'ai_videototext',
    component: PromptPlayground,
    adGroups: (env) => fetchPageEntitlements(env, 'ai_videototext'),
    exact: true,
  },
  {
    path: BULK_TESTS_VIEW_AI,
    routeName: 'bulktests',
    component: BulkTestsListView,
    adGroups: (env) => fetchPageEntitlements(env, 'ai_imagetotext_bulk'),
    exact: true,
  },
  {
    path: '/ai_imagetotext/bulktests/new',
    routeName: 'bulktesting_newtest',
    component: NewBulkTestView,
    adGroups: (env) => fetchPageEntitlements(env, 'ai_imagetotext_bulk'),
    exact: true,
  },
  {
    path: '/ai_imagetotext/bulktests/:bulktestid',
    routeName: 'bulktesting_newtest',
    component: BulkTestReviewPage,
    adGroups: (env) => fetchPageEntitlements(env, 'ai_imagetotext_bulk'),
    exact: true,
  },
  {
    path: '/unauthorized',
    name: 'UnAuthorized',
    tooltip: 'UnAuthorized',
    displayName: '',
    component: Unauthorized,
    exact: true,
    adGroups: undefined,
  },
  {
    path: '*',
    name: 'Missed Routes',
    component: NotFound,
    adGroups: (env) => fetchPageEntitlements(env, 'home'),
  },
]

export default navRoutes
